import { motion } from "framer-motion";
import { Plus, Zap, Wrench, ShieldCheck } from "lucide-react";
import { Reveal, Eyebrow, CountUp } from "@/components/primitives";

const FacilityCard = ({ value, label, testId }) => (
  <div
    data-testid={testId}
    className="flex-1 rounded-2xl border border-gold/40 bg-graphite/70 p-6 backdrop-blur-md"
  >
    <div className="gold-text font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
      <CountUp to={value} />
    </div>
    <div className="mt-1 text-[11px] font-semibold uppercase tracking-[0.25em] text-ivory/50">
      Sq.Ft
    </div>
    <div className="mt-3 h-px w-10 bg-gold/60" />
    <div className="mt-3 font-display text-sm font-bold uppercase tracking-[0.18em] text-ivory">
      {label}
    </div>
  </div>
);

const BAY_POINTS = [
  { icon: Zap, label: "Faster Turnaround" },
  { icon: Wrench, label: "Skilled Technicians" },
  { icon: ShieldCheck, label: "Reliable Care" },
];

export default function Stats() {
  return (
    <>
      <section
        id="facility"
        data-testid="facility-section"
        className="relative overflow-hidden bg-espresso py-24 sm:py-32"
      >
        <div className="absolute inset-0">
          <img
            src="/images/hero.webp"
            alt=""
            aria-hidden="true"
            loading="lazy"
            className="h-full w-full object-cover object-[68%_center] opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-espresso via-espresso/60 to-espresso" />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 sm:px-10">
          <Reveal y={48}>
            <Eyebrow index="01">Our Facility</Eyebrow>
          </Reveal>
          <Reveal delay={0.12} y={60} className="mt-8">
            <div
              data-testid="facility-total"
              className="gold-text font-display text-[22vw] font-black leading-none tracking-tighter sm:text-8xl lg:text-9xl"
            >
              <CountUp to={50000} />
            </div>
            <div className="mt-2 font-display text-sm font-bold uppercase tracking-[0.4em] text-ivory/70 sm:text-base">
              Sq.Ft — Total Facility
            </div>
          </Reveal>
          <Reveal delay={0.26} y={60} className="mt-14">
            <div className="flex items-stretch gap-3 sm:max-w-2xl sm:gap-5">
              <FacilityCard value={30000} label="Workshop Space" testId="facility-workshop-card" />
              <div className="flex items-center">
                <span className="flex h-9 w-9 items-center justify-center rounded-full border border-gold/50 text-gold">
                  <Plus size={16} />
                </span>
              </div>
              <FacilityCard value={20000} label="Parking Space" testId="facility-parking-card" />
            </div>
            <p className="mt-6 text-xs font-medium uppercase tracking-[0.25em] text-ivory/40">
              30,000 + 20,000 — engineered as one seamless facility
            </p>
          </Reveal>
        </div>
      </section>

      <section
        id="capacity"
        data-testid="capacity-section"
        className="border-t border-gold/10 bg-espresso py-20 text-ivory sm:py-28"
      >
        <div className="mx-auto grid max-w-7xl gap-12 px-6 sm:px-10 md:grid-cols-2 md:items-center">
          <div>
            <Reveal y={44}>
              <Eyebrow index="02">Service Capacity</Eyebrow>
            </Reveal>
            <Reveal delay={0.12} y={56} className="mt-8">
              <div className="flex items-end gap-4">
                <span
                  data-testid="bays-number"
                  className="gold-text font-display text-[26vw] font-black leading-[0.85] tracking-tighter sm:text-9xl"
                >
                  <CountUp to={30} />
                </span>
                <span className="pb-2 font-display text-base font-bold uppercase leading-tight tracking-[0.25em] text-ivory sm:text-lg">
                  Service
                  <br />
                  Bays
                </span>
              </div>
            </Reveal>
            <Reveal delay={0.22} y={44}>
              <p className="mt-6 max-w-sm text-base font-light leading-relaxed text-ivory/60">
                Built for scale. Designed for precision.
              </p>
            </Reveal>
            <Reveal delay={0.32} y={44} className="mt-8 space-y-4">
              {BAY_POINTS.map((p) => (
                <div key={p.label} className="flex items-center gap-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full border border-gold/40 text-gold">
                    <p.icon size={16} strokeWidth={1.75} />
                  </span>
                  <span className="text-xs font-bold uppercase tracking-[0.22em] text-ivory/80">
                    {p.label}
                  </span>
                </div>
              ))}
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <motion.div
              className="relative overflow-hidden rounded-2xl"
              whileHover={{ scale: 0.985 }}
              transition={{ duration: 0.6 }}
            >
              <img
                src="/images/bays.webp"
                alt="Row of premium service bays at V-Elite Drives Tirupur"
                loading="lazy"
                className="aspect-[4/5] w-full object-cover sm:aspect-[4/3] md:aspect-[4/5]"
              />
              <div className="absolute inset-0 rounded-2xl border border-gold/20" />
            </motion.div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
